import { useMutation, useQueryClient } from '@tanstack/react-query';
import { captureException, showNotification } from 'r/utils';
import {
  fetchApiRaw,
  POST_ANALYSES_VERIFY,
  DELETE_ANALYSES,
  PUT_ANALYSES,
} from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';

export function useBulkVerifyAnalyses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (analysisIds: string[]) => {
      const accessToken = StoredAuth.get();
      await fetchApiRaw(
        POST_ANALYSES_VERIFY,
        { body: [...analysisIds] },
        accessToken,
      );
    },
    onSuccess: (_data, analysisIds) => {
      showNotification('success', `Verified ${analysisIds.length} analyses`);
      queryClient.invalidateQueries({ queryKey: ['admin-analyses'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to verify analyses');
    },
  });
}

export function useBulkDeleteAnalyses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (analysisIds: string[]) => {
      const accessToken = StoredAuth.get();
      await Promise.all(
        analysisIds.map((id) =>
          fetchApiRaw(DELETE_ANALYSES, { urlParams: [id] }, accessToken),
        ),
      );
    },
    onSuccess: (_data, analysisIds) => {
      showNotification('success', `Deleted ${analysisIds.length} analyses`);
      queryClient.invalidateQueries({ queryKey: ['admin-analyses'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Some analyses could not be deleted');
    },
  });
}

export function useUpdateAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      analysisId,
      body,
    }: {
      analysisId: string;
      body: Record<string, unknown>;
    }) => {
      const accessToken = StoredAuth.get();
      await fetchApiRaw(
        PUT_ANALYSES,
        { urlParams: [analysisId], body },
        accessToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-analyses'] });
    },
    onError: (error) => {
      captureException(error);
    },
  });
}

export function useDeleteAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (analysisId: string) => {
      const accessToken = StoredAuth.get();
      await fetchApiRaw(
        DELETE_ANALYSES,
        { urlParams: [analysisId] },
        accessToken,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Analysis deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-analyses'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to delete analysis');
    },
  });
}
